"use client";

import { useEffect, useMemo, useState } from "react";

import { GridTee } from "@/components/ui/grid-connector";

import type { PressReleaseCategoryFilter, PressReleaseItem } from "./data";
import { PressRoomAccordionList } from "./press-room-accordion-list";
import {
  PressRoomFilterRail,
  useSelectedPressReleaseCategories,
} from "./press-room-filter-rail";

const RELEASES_PER_BATCH = 20;

interface PressRoomContentColumnProps {
  releases: PressReleaseItem[];
  filters: PressReleaseCategoryFilter[];
  totalCount: number;
}

/**
 * Press releases section (Figma 5863:26726) — sits flush under the hero band.
 * The page rails continue from the hero; the filter rail pins under the nav in
 * the left column and scrolls away with the listing. The rows column carries a
 * full-height left divider.
 */
export function PressRoomContentColumn({
  releases,
  filters,
  totalCount,
}: PressRoomContentColumnProps) {
  const selectedCategories = useSelectedPressReleaseCategories();
  const [visibleCount, setVisibleCount] = useState(RELEASES_PER_BATCH);

  const filteredReleases = useMemo(() => {
    if (!selectedCategories.length) return releases;
    return releases.filter((release) =>
      release.categories.some((category) =>
        selectedCategories.includes(category)
      )
    );
  }, [releases, selectedCategories]);

  useEffect(() => {
    setVisibleCount(RELEASES_PER_BATCH);
  }, [selectedCategories]);

  const visibleReleases = filteredReleases.slice(0, visibleCount);
  const hasMore = filteredReleases.length > visibleCount;

  return (
    <div className="bg-grey-50 border-grey-100 relative lg:border-t lg:border-b">
      <div className="relative mx-auto max-w-[1440px]">
        <div className="bg-grey-0 border-grey-100 relative grid grid-cols-1 lg:mx-20 lg:grid-cols-[217px_minmax(0,1fr)] lg:border-x lg:border-b">
          {/* Figma 5863:26726 / page 7516:39417–39421 — ⊤ marks where the
              section top edge crosses the page rails (80px inset). */}
          <GridTee className="text-grey-950 pointer-events-none absolute -top-[0.5px] -left-[3.5px] z-10 hidden lg:block" />
          <GridTee className="text-grey-950 pointer-events-none absolute -top-[0.5px] -right-[3.5px] z-10 hidden lg:block" />

          <aside className="relative lg:p-0">
            <PressRoomFilterRail filters={filters} totalCount={totalCount} />
          </aside>

          <div className="border-grey-100 relative lg:border-l">
            <PressRoomAccordionList releases={visibleReleases} />

            {filteredReleases.length === 0 ? (
              <div className="px-10 py-16 text-center">
                <p className="text-ds-body-md text-grey-500">
                  No releases match your filters.
                </p>
              </div>
            ) : null}
          </div>
        </div>

        {hasMore ? (
          <div className="flex justify-center pt-10">
            <button
              type="button"
              onClick={() =>
                setVisibleCount((count) => count + RELEASES_PER_BATCH)
              }
              className="bg-grey-0 border-grey-100 text-ds-label-md text-grey-950 rounded-ds-full hover:bg-grey-50 inline-flex cursor-pointer items-center border px-4 py-2 font-medium transition-colors"
            >
              Load more
            </button>
          </div>
        ) : null}

        <div aria-hidden className="h-20" />
      </div>
    </div>
  );
}
